/**
 * Guide-Me Panel Component - Sprint 16
 * Contextual help panel for Demand View
 * 
 * Features:
 * - Current stage progress indicator
 * - Required next steps with deep links
 * - FAQ section
 * - AI-powered helpers (rewrite, summarize, suggest benefits, identify risks)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  HelpCircle, ChevronRight, ChevronDown, MapPin, Target, FileQuestion,
  Sparkles, RefreshCw, Copy, Check, X, ExternalLink, AlertTriangle,
  Edit3, FileText, TrendingUp, Shield, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stage progress configuration
const STAGE_PROGRESS = [
  { id: 'new', label: 'New', percentage: 10 },
  { id: 'draft', label: 'Draft', percentage: 25 },
  { id: 'intake', label: 'Submitted', percentage: 40 },
  { id: 'submitted', label: 'Submitted', percentage: 50 },
  { id: 'in_review', label: 'In Review', percentage: 65 },
  { id: 'assessment', label: 'Assessment', percentage: 65 },
  { id: 'governance', label: 'Governance', percentage: 75 },
  { id: 'approved', label: 'Approved', percentage: 85 },
  { id: 'converted', label: 'Converted', percentage: 100 },
  { id: 'rejected', label: 'Rejected', percentage: 100 }
];

// Progress Step Component
const ProgressStep = ({ stage, currentStage, index, totalSteps }) => {
  const isActive = stage.id === currentStage;
  const stageIndex = STAGE_PROGRESS.findIndex(s => s.id === currentStage);
  const thisIndex = STAGE_PROGRESS.findIndex(s => s.id === stage.id);
  const isPast = thisIndex < stageIndex;
  const isFuture = thisIndex > stageIndex;
  
  return (
    <div className="flex items-center gap-2">
      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
        isActive ? 'bg-blue-600 text-white' :
        isPast ? 'bg-green-500 text-white' :
        'bg-gray-200 text-gray-500'
      }`}>
        {isPast ? <Check className="w-3 h-3" /> : index + 1}
      </div>
      <span className={`text-sm ${isActive ? 'font-medium text-blue-700' : 'text-gray-600'}`}>
        {stage.label}
      </span>
    </div>
  );
};

// Missing Field Link Component
const MissingFieldLink = ({ field, demandId }) => {
  const handleClick = () => {
    // Focus on the field in the form
    const element = document.querySelector(`[name="${field.field}"]`);
    if (element) {
      element.focus();
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };
  
  return (
    <button
      onClick={handleClick}
      className="flex items-center gap-2 w-full px-3 py-2 bg-amber-50 hover:bg-amber-100 rounded-lg text-sm text-amber-800 transition-colors"
    >
      <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
      <span>{field.label}</span>
      <ExternalLink className="w-3 h-3 ml-auto text-amber-500" />
    </button>
  );
};

// FAQ Item Component
const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border rounded-lg">
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
    >
      <span className="text-left">{question}</span>
      {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
    </button>
    {isOpen && (
      <div className="px-3 pb-3 text-sm text-gray-600 border-t">
        {answer}
      </div>
    )}
  </div>
);

// AI Action Button Component
const AIActionButton = ({ icon: Icon, label, action, loading, onClick, color = 'purple' }) => (
  <button
    onClick={onClick}
    disabled={loading}
    className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors
      ${loading ? 'bg-gray-100 text-gray-400 cursor-wait' :
        `bg-${color}-50 hover:bg-${color}-100 text-${color}-700`
      }`}
  >
    {loading ? (
      <Loader2 className="w-4 h-4 animate-spin" />
    ) : (
      <Icon className={`w-4 h-4 text-${color}-500`} />
    )}
    <span>{label}</span>
    {loading && <span className="ml-auto text-xs text-gray-400">Processing...</span>}
  </button>
);

// AI Result Card Component
const AIResultCard = ({ title, content, onInsert, onCopy, onDismiss }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    const textContent = typeof content === 'string' ? content : 
      Array.isArray(content) ? content.join('\n') : JSON.stringify(content, null, 2);
    navigator.clipboard.writeText(textContent);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-medium text-purple-800 text-sm">{title}</h4>
        <button onClick={onDismiss} className="p-1 hover:bg-purple-100 rounded">
          <X className="w-3 h-3 text-purple-500" />
        </button>
      </div>
      
      <div className="text-sm text-purple-900 whitespace-pre-wrap mb-3">
        {typeof content === 'string' ? content : 
          Array.isArray(content) ? (
            <ul className="list-disc pl-4 space-y-1">
              {content.map((item, i) => <li key={i}>{typeof item === 'object' ? item.risk || item.benefit || JSON.stringify(item) : item}</li>)}
            </ul>
          ) : (
            <pre className="text-xs">{JSON.stringify(content, null, 2)}</pre>
          )
        }
      </div>
      
      <div className="flex gap-2">
        {onInsert && (
          <button
            onClick={onInsert}
            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-purple-600 rounded hover:bg-purple-700"
          >
            <Check className="w-3 h-3" />
            Insert
          </button>
        )}
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-purple-700 bg-white border border-purple-300 rounded hover:bg-purple-50"
        >
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
    </div>
  );
};

// Main Guide-Me Panel Component
const GuideMePanel = ({ demandId, demandData, isOpen, onToggle, onUpdateDemand }) => {
  const [guidance, setGuidance] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [aiLoading, setAiLoading] = useState(null);
  const [aiResults, setAiResults] = useState({});
  const [expandedSection, setExpandedSection] = useState('progress');
  
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('simpliview_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }, []);
  
  // Fetch guidance data
  const fetchGuidance = useCallback(async () => {
    if (!demandId) return;
    
    setLoading(true);
    try {
      // Fetch where_am_i and what_next
      const [whereRes, whatRes, faqRes] = await Promise.all([
        fetch(`${API_URL}/api/ai/demand-guide`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ demand_id: demandId, action: 'where_am_i' })
        }),
        fetch(`${API_URL}/api/ai/demand-guide`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ demand_id: demandId, action: 'what_next' })
        }),
        fetch(`${API_URL}/api/ai/demand-guide/faqs`, { headers: getAuthHeaders() })
      ]);
      
      if (whereRes.ok && whatRes.ok) {
        const whereData = await whereRes.json();
        const whatData = await whatRes.json();
        
        setGuidance({
          where: whereData.result,
          what: whatData.result
        });
      }
      
      if (faqRes.ok) {
        const faqData = await faqRes.json();
        setFaqs(faqData.faqs || []);
      }
    } catch (err) {
      console.error('Failed to fetch guidance:', err);
    } finally {
      setLoading(false);
    }
  }, [demandId, getAuthHeaders]);
  
  useEffect(() => {
    if (isOpen && demandId) {
      fetchGuidance();
    }
  }, [isOpen, demandId, fetchGuidance]);
  
  // AI Actions
  const handleAIAction = async (action) => {
    setAiLoading(action);
    
    try {
      const context = {
        title: demandData?.title || '',
        description: demandData?.description || '',
        business_case: demandData?.business_case || ''
      };
      
      let inputText = '';
      if (action === 'rewrite') inputText = demandData?.description || '';
      if (action === 'summarize') inputText = demandData?.business_case || '';
      
      const res = await fetch(`${API_URL}/api/ai/demand-guide`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          demand_id: demandId,
          action: action,
          context: context,
          input_text: inputText
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setAiResults(prev => ({
          ...prev,
          [action]: data.result
        }));
        toast.success('AI suggestion generated');
      } else {
        toast.error('Failed to get AI suggestion');
      }
    } catch (err) {
      console.error('AI action failed:', err);
      toast.error('AI request failed');
    } finally {
      setAiLoading(null);
    }
  };
  
  const clearAiResult = (action) => {
    setAiResults(prev => {
      const updated = { ...prev };
      delete updated[action];
      return updated;
    });
  };
  
  // Handle insert AI result into demand
  const handleInsertResult = (action, field, value) => {
    if (onUpdateDemand) {
      onUpdateDemand({ [field]: value });
      toast.success(`Updated ${field}`);
      clearAiResult(action);
    }
  };
  
  if (!isOpen) {
    return (
      <button
        onClick={onToggle}
        className="fixed right-4 top-1/2 -translate-y-1/2 z-40 bg-gradient-to-r from-purple-600 to-blue-600 text-white p-3 rounded-l-lg shadow-lg hover:from-purple-700 hover:to-blue-700 transition-colors"
        data-testid="guide-me-toggle"
      >
        <HelpCircle className="w-5 h-5" />
      </button>
    );
  }
  
  const currentStatus = demandData?.status || 'new';
  const currentStageInfo = STAGE_PROGRESS.find(s => s.id === currentStatus) || STAGE_PROGRESS[0];
  
  return (
    <div className="fixed right-0 top-16 bottom-0 w-80 bg-white border-l shadow-xl z-40 flex flex-col overflow-hidden" data-testid="guide-me-panel">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <HelpCircle className="w-5 h-5" />
          <span className="font-semibold">Guide-Me</span>
        </div>
        <button onClick={onToggle} className="p-1 hover:bg-white/20 rounded">
          <X className="w-4 h-4" />
        </button>
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
          </div>
        ) : (
          <>
            {/* Where Am I Section */}
            <div className="bg-gray-50 rounded-lg p-3">
              <button
                onClick={() => setExpandedSection(expandedSection === 'progress' ? null : 'progress')}
                className="w-full flex items-center justify-between text-sm font-medium text-gray-700 mb-2"
              >
                <span className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-blue-500" />
                  Where Am I?
                </span>
                {expandedSection === 'progress' ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
              
              {expandedSection === 'progress' && (
                <>
                  <div className="mb-3">
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                      <span>Progress</span>
                      <span>{currentStageInfo.percentage}%</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-purple-500 to-blue-500 transition-all"
                        style={{ width: `${currentStageInfo.percentage}%` }}
                      />
                    </div>
                  </div>
                  
                  <div className="bg-white rounded p-2 border">
                    <div className="text-sm font-medium text-gray-800">
                      {guidance?.where?.current_stage || currentStageInfo.label}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {guidance?.where?.stage_description || 'Processing your demand...'}
                    </p>
                  </div>
                </>
              )}
            </div>
            
            {/* What's Required Next Section */}
            <div className="bg-gray-50 rounded-lg p-3">
              <button
                onClick={() => setExpandedSection(expandedSection === 'next' ? null : 'next')}
                className="w-full flex items-center justify-between text-sm font-medium text-gray-700 mb-2"
              >
                <span className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-green-500" />
                  What's Required Next?
                </span>
                {expandedSection === 'next' ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
              
              {expandedSection === 'next' && (
                <div className="space-y-2">
                  {guidance?.what?.missing_fields?.length > 0 ? (
                    guidance.what.missing_fields.map((field, idx) => (
                      <MissingFieldLink key={idx} field={field} demandId={demandId} />
                    ))
                  ) : (
                    <div className="text-sm text-green-700 bg-green-50 p-2 rounded flex items-center gap-2">
                      <Check className="w-4 h-4" />
                      All required fields complete!
                    </div>
                  )}
                  
                  {guidance?.what?.next_actions?.length > 0 && (
                    <div className="mt-2 pt-2 border-t">
                      <div className="text-xs font-medium text-gray-500 mb-1">Next Actions:</div>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {guidance.what.next_actions.map((action, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <ChevronRight className="w-3 h-3 mt-1 text-gray-400" />
                            {action}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* FAQ Section */}
            <div className="bg-gray-50 rounded-lg p-3">
              <button
                onClick={() => setExpandedSection(expandedSection === 'faq' ? null : 'faq')}
                className="w-full flex items-center justify-between text-sm font-medium text-gray-700 mb-2"
              >
                <span className="flex items-center gap-2">
                  <FileQuestion className="w-4 h-4 text-amber-500" />
                  FAQ
                </span>
                {expandedSection === 'faq' ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
              
              {expandedSection === 'faq' && (
                <div className="space-y-2">
                  {faqs.slice(0, 5).map((faq, idx) => (
                    <FAQItem
                      key={idx}
                      question={faq.question}
                      answer={faq.answer}
                      isOpen={expandedFaq === idx}
                      onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                    />
                  ))}
                </div>
              )}
            </div>
            
            {/* AI Helpers Section */}
            <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-3 border border-purple-200">
              <div className="flex items-center gap-2 text-sm font-medium text-purple-700 mb-3">
                <Sparkles className="w-4 h-4" />
                AI Helpers
              </div>
              
              <div className="space-y-2">
                <AIActionButton
                  icon={Edit3}
                  label="Rewrite Description"
                  action="rewrite"
                  loading={aiLoading === 'rewrite'}
                  onClick={() => handleAIAction('rewrite')}
                />
                
                <AIActionButton
                  icon={FileText}
                  label="Summarize Business Case"
                  action="summarize"
                  loading={aiLoading === 'summarize'}
                  onClick={() => handleAIAction('summarize')}
                />
                
                <AIActionButton
                  icon={TrendingUp}
                  label="Suggest Benefits"
                  action="suggest_benefits"
                  loading={aiLoading === 'suggest_benefits'}
                  onClick={() => handleAIAction('suggest_benefits')}
                />
                
                <AIActionButton
                  icon={Shield}
                  label="Identify Risks"
                  action="identify_risks"
                  loading={aiLoading === 'identify_risks'}
                  onClick={() => handleAIAction('identify_risks')}
                />
              </div>
            </div>
            
            {/* AI Results */}
            {Object.keys(aiResults).length > 0 && (
              <div className="space-y-3">
                {aiResults.rewrite?.rewritten && (
                  <AIResultCard
                    title="Rewritten Description"
                    content={aiResults.rewrite.rewritten}
                    onInsert={() => handleInsertResult('rewrite', 'description', aiResults.rewrite.rewritten)}
                    onDismiss={() => clearAiResult('rewrite')}
                  />
                )}
                
                {aiResults.summarize?.summary && (
                  <AIResultCard
                    title="Summary"
                    content={aiResults.summarize.summary}
                    onDismiss={() => clearAiResult('summarize')}
                  />
                )}
                
                {aiResults.suggest_benefits?.suggested_benefits && (
                  <AIResultCard
                    title="Suggested Benefits"
                    content={aiResults.suggest_benefits.suggested_benefits}
                    onDismiss={() => clearAiResult('suggest_benefits')}
                  />
                )}
                
                {aiResults.identify_risks?.identified_risks && (
                  <AIResultCard
                    title="Identified Risks"
                    content={aiResults.identify_risks.identified_risks}
                    onDismiss={() => clearAiResult('identify_risks')}
                  />
                )}
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Footer */}
      <div className="px-4 py-2 border-t bg-gray-50 text-xs text-gray-500 text-center">
        AI suggestions are for guidance only
      </div>
    </div>
  );
};

export default GuideMePanel;
